import React from 'react';
import styled, {css} from 'styled-components';

import {Text} from '@famly/mf_data-display_text';
import {Stack} from '@famly/mf_layout_stack';
import {Box} from '@famly/mf_layout_box';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import Tooltip from 'web-app/react/components/tooltip/tooltip';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';
import {checkIfFlagIsToggled, DebugLocalStorageFlags} from 'web-app/react/routes/debugger/local-storage-config';

import type * as Types from '../types';

type SummaryPeriodItemProps = Exclude<Types.SummaryPeriodItem, 'prorated'> & {
    billingInfoByPricingGroup: Array<Types.SummaryBillingInfo>;
    currency: string;
    isDiscount?: boolean;
    extraHigh?: boolean;
    emphasizePrice: boolean;
    testId: string;
};

const SummaryPeriodItem = ({
    name,
    pricingGroups,
    billingInfoByPricingGroup,
    currency,
    isDiscount = false,
    extraHigh = false,
    emphasizePrice,
    testId,
}: SummaryPeriodItemProps) => {
    const discountMinusSign = isDiscount ? '- ' : '';

    return (
        <tr data-e2e-class="summary-period-item" data-e2e-id={testId}>
            <TD>
                <Text
                    pb={2}
                    variant="body-small"
                    color="n300"
                    data-e2e-class="summary-period-item-name"
                    data-e2e-id={`${testId}-name`}
                    marginBottom={extraHigh ? 1 : 0}
                >
                    {name}
                </Text>
            </TD>
            {pricingGroups.map((pricingGroup, index) => {
                const billingInfo = billingInfoByPricingGroup[index] ?? {
                    isAlreadyBilled: false,
                    isInvoiceValues: false,
                };
                return (
                    <PricingGroup
                        key={`pricingGroup-${index}`}
                        pricingGroup={pricingGroup}
                        billingInfo={billingInfo}
                        currency={currency}
                        discountMinusSign={discountMinusSign}
                        emphasizePrice={emphasizePrice}
                        testId={`${testId}-pricing-group-${pricingGroup.id}`}
                    />
                );
            })}
        </tr>
    );
};

type PricingGroupsProps = {
    pricingGroup: Types.SummaryPeriodItem['pricingGroups'][number];
    billingInfo: Types.SummaryBillingInfo;
    currency: string;
    discountMinusSign: string;
    emphasizePrice: boolean;
    testId: string;
};
const PricingGroup = ({
    pricingGroup,
    billingInfo,
    currency,
    discountMinusSign,
    emphasizePrice,
    testId,
}: PricingGroupsProps) => {
    const priceElement = React.useMemo(() => {
        const defaultProps = {
            pb: 2,
            variant: 'body-small' as const,
            color: 'n300' as const,
            emphasized: emphasizePrice,
            'data-e2e-class': 'summary-period-item-price',
            'data-e2e-id': `${testId}-price`,
        };

        if (billingInfo.isAlreadyBilled && billingInfo.priceQuality !== 'invoiced') {
            const reason = needsTranslations(
                'SimplifiedUsBilling',
                'The exact amount that has been billed is not available.',
            );
            return (
                <Tooltip trigger="hover" placement="left" overlay={<Text variant="body-small">{reason}</Text>}>
                    <Box>
                        <Text width={'fit-content'} {...defaultProps}>
                            —
                        </Text>
                    </Box>
                </Tooltip>
            );
        }

        if (billingInfo.priceQuality === 'unknown') {
            const showExtraTestingInformation = checkIfFlagIsToggled(
                DebugLocalStorageFlags.CHILL_BILL_EDIT_PLAN_TESTING_INFORMATION.flag,
            );
            const reason = needsTranslations(
                'SimplifiedUsBilling',
                'The amount will be calculated at the time of invoicing.',
            );
            const deeperReason = showExtraTestingInformation ? getDeeperReason(billingInfo.priceQualityReason) : null;

            const reasonElement = (
                <>
                    <Text variant="body-small">{reason}</Text>
                    {hasValue(deeperReason) ? <Text variant="body-small">{deeperReason}</Text> : null}
                </>
            );
            return (
                <Tooltip trigger="hover" placement="left" overlay={reasonElement}>
                    <Box>
                        <Text width={'fit-content'} {...defaultProps}>
                            —
                        </Text>
                    </Box>
                </Tooltip>
            );
        }

        if (isNaN(pricingGroup.price)) {
            return null;
        }

        const approximateString = billingInfo.priceQuality === 'approximate' ? '≈ ' : '';

        return (
            <Text {...defaultProps}>
                {`${discountMinusSign}${currency} ${approximateString}${pricingGroup.price.toFixed(2)}`}
            </Text>
        );
    }, [
        billingInfo.isAlreadyBilled,
        billingInfo.priceQuality,
        billingInfo.priceQualityReason,
        pricingGroup.price,
        discountMinusSign,
        currency,
        emphasizePrice,
        testId,
    ]);

    return (
        <TD rightAlign>
            <Stack justifyContent="end">
                {hasValue(pricingGroup.children) && pricingGroup.children.length > 0 ? (
                    <Tooltip
                        trigger={['hover']}
                        placement="top"
                        overlay={pricingGroup.children.map(childName => (
                            <Text data-e2e-id={`${testId}-price-tooltip-text`} key={childName} variant="body-small">
                                {childName}
                            </Text>
                        ))}
                    >
                        <Box>{priceElement}</Box>
                    </Tooltip>
                ) : (
                    priceElement
                )}
            </Stack>
        </TD>
    );
};

const getDeeperReason = (reason: Types.PriceQualityReason | undefined): string | undefined => {
    switch (reason) {
        case 'customPeriod':
            return 'The period is partial';
        case 'multiplePlans':
            return 'Multiple plans overlap with the period.';
        case 'noPlans':
            return 'There are no plans that overlap with the period.';
        case 'noBillingFrequency':
            return 'No billing frequency selected.';
        case 'billingFrequencyMismatch':
            return 'The bill payer billing frequency does not match the plan frequency.';
        case 'payableBillingFrequencyMismatch':
            return 'The bill payer billing frequency does not match the frequency of a tuition, fee, or discount.';
        case undefined:
            return undefined;
        default:
            exhaustiveCheck(reason);
            return undefined;
    }
};

const TD = styled.td<{rightAlign?: boolean}>`
    padding: 0;
    cursor: default;
    ${({rightAlign}) =>
        rightAlign
            ? css`
                  text-align: right;
              `
            : ''};
`;

export {SummaryPeriodItem};
